import { Link } from "react-router-dom"
import { LegalList, LegalPage, LegalSection } from "../lib/shared"
import { Seo, breadcrumbSchema } from "../lib/seo"
import { SITE } from "../lib/site"

/** Bump whenever the substance of this policy changes. */
export const PRIVACY_UPDATED = "2026-09-15"

export default function Privacy() {
  return (
    <>
      <Seo
        title="Privacy Policy"
        description="How Sableboxx collects, uses, stores and protects personal information: what we hold, why we hold it, who we share it with and the rights you have over it."
        jsonLd={[breadcrumbSchema([{ name: "Privacy Policy", path: "/privacy" }])]}
      />
      <LegalPage
        title="Privacy Policy"
        updated={PRIVACY_UPDATED}
        intro="This policy explains what personal information Sableboxx collects, why we collect it, how long we keep it and what you can ask us to do with it. It is written to be read, not to be endured."
      >
        <LegalSection n={1} heading="Who we are">
          <p>
            Sableboxx (&ldquo;we&rdquo;, &ldquo;us&rdquo;) is a design and
            brand studio operating from {SITE.address.city},{" "}
            {SITE.address.countryName}, and working with clients worldwide. We
            are the data controller for the information described in this
            policy.
          </p>
          <p>
            For anything in this policy, including requests about your own data,
            write to <a href={`mailto:${SITE.email}`}>{SITE.email}</a> or call{" "}
            <a href={`tel:${SITE.phoneHref}`}>{SITE.phone}</a>.
          </p>
        </LegalSection>

        <LegalSection n={2} heading="What we collect">
          <p>
            We only collect information you give us or that our hosting
            infrastructure records automatically in the course of serving the
            site.
          </p>
          <p>
            <strong>When you submit the enquiry form</strong> at{" "}
            <Link to="/contact">/contact</Link> we receive your name, email
            address, company name, website (if you give one), the service you
            selected, the budget range you selected and the message you wrote.
            All of it is optional except your name, your email address and your
            message, which the form needs before it can be sent.
          </p>
          <p>
            <strong>When you email or call us</strong> we hold the contents of
            that correspondence and your contact details.
          </p>
          <p>
            <strong>Automatically</strong>, our host records standard server
            logs: IP address, user agent, the page requested and the time of the
            request. We use these for security and to keep the site running. We
            do not build advertising profiles and we do not sell data.
          </p>
        </LegalSection>

        <LegalSection n={3} heading="Why we collect it, and on what basis">
          <LegalList
            items={[
              <>
                <strong>To reply to your enquiry and scope work.</strong> Our
                lawful basis is your request: taking steps at your request
                before entering a contract.
              </>,
              <>
                <strong>To deliver a project you have engaged us for.</strong>{" "}
                Our lawful basis is performance of that contract.
              </>,
              <>
                <strong>To keep the site secure and available</strong>, and to
                keep accounting records we are required to keep. Our lawful
                basis is our legitimate interest and, for records, legal
                obligation.
              </>,
            ]}
          />
          <p>
            We do not send marketing email to people who have only submitted an
            enquiry. If we ever run a mailing list it will be opt-in and
            separately consented to.
          </p>
        </LegalSection>

        <LegalSection n={4} heading="Cookies and tracking">
          <p>
            The public Sableboxx website sets <strong>no</strong> advertising or
            analytics cookies, and runs no third-party tracking scripts. You
            will not see a cookie banner here because there is nothing to
            consent to.
          </p>
          <p>
            The studio dashboard at <code>/admin</code>, which only Sableboxx
            staff can reach, stores a sign-in token in the browser so that a
            signed-in session survives a page reload. It is strictly necessary
            for that tool to function and is never set for public visitors.
          </p>
        </LegalSection>

        <LegalSection n={5} heading="Who processes data on our behalf">
          <p>
            We keep the list of third parties deliberately short. Each is a
            processor acting on our instructions, not an independent recipient
            of your data.
          </p>
          <LegalList
            items={[
              <>
                <strong>Netlify</strong> hosts and serves this website, and
                records the server logs described above.
              </>,
              <>
                <strong>Render</strong> runs the application that receives
                enquiry form submissions.
              </>,
              <>
                <strong>MongoDB Atlas</strong> stores enquiries, client
                records and site content.
              </>,
              <>
                <strong>Our email provider</strong> delivers the notification
                we receive when you submit the form, and our replies to you.
              </>,
              <>
                <strong>Fontshare and Unsplash</strong> serve the typeface and
                some imagery. Requesting a file from them discloses your IP
                address to them, as it would for any file on any website.
              </>,
            ]}
          />
          <p>
            Some of these providers operate servers outside Nigeria and outside
            the EEA. Where they do, transfers rely on the provider&rsquo;s
            standard contractual clauses and equivalent safeguards.
          </p>
        </LegalSection>

        <LegalSection n={6} heading="How long we keep it">
          <LegalList
            items={[
              <>
                <strong>Enquiries that do not become projects</strong> are kept for
                24 months, then deleted, so we can recognise you if you come
                back.
              </>,
              <>
                <strong>Client records and project correspondence</strong> are
                kept for the life of the engagement and for 7 years afterwards,
                which is how long we must keep the associated financial records.
              </>,
              <>
                <strong>Server logs</strong> are retained by our host on their own
                rolling schedule, typically 30 days.
              </>,
            ]}
          />
        </LegalSection>

        <LegalSection n={7} heading="Your rights">
          <p>
            Under the Nigeria Data Protection Act 2023, and under the UK and EU
            GDPR where they apply to you, you can ask us to:
          </p>
          <LegalList
            items={[
              "Give you a copy of the personal data we hold about you.",
              "Correct anything inaccurate.",
              "Delete it, where we have no continuing obligation to keep it.",
              "Restrict or object to how we use it.",
              "Send it to you, or to someone else, in a portable format.",
            ]}
          />
          <p>
            Email <a href={`mailto:${SITE.email}`}>{SITE.email}</a> and we will
            respond within 30 days. There is no charge. If you are unsatisfied
            with our response you may complain to the Nigeria Data Protection
            Commission, or to the supervisory authority where you live.
          </p>
        </LegalSection>

        <LegalSection n={8} heading="Security">
          <p>
            Data is transmitted over HTTPS, stored in an access-controlled
            database, and the studio dashboard requires a password that is
            stored only as a salted hash. Access is limited to the Sableboxx
            people who need it. No system is perfect; if a breach ever affects
            your data we will notify you and the relevant regulator as the law
            requires.
          </p>
        </LegalSection>

        <LegalSection n={9} heading="Changes to this policy">
          <p>
            If we change this policy we will update the date at the top of this
            page. Material changes affecting people whose data we already hold
            will be communicated directly by email.
          </p>
        </LegalSection>
      </LegalPage>
    </>
  )
}
